import {
  CrmFieldMatch,
  CustomFieldTypeEnum,
  FieldMappingBaseFieldSchema,
  FieldMappingBaseFieldType,
  FieldMappingBatchUpsertPayloadMappingFieldType,
  FieldMappingBatchUpsertPayloadType,
  FieldMappingCreateFieldType,
  FieldMappingCreateReqType,
  FieldMappingObjectType,
  FieldMappingsResFieldType,
  FieldMappingsResType,
  FieldMappingSystemType,
  FieldMappingType,
} from '@monetize/types/app';
import isArray from 'lodash/isArray';
import orderBy from 'lodash/orderBy';
import startCase from 'lodash/startCase';
import {
  CRM_FIELD_MAPPING_OBJECT_TYPES,
  MN_ACCOUNT_OUTBOUND_FIELDS_FOR_SALESFORCE,
  SF_ACCOUNT_OUTBOUND_EXCLUDED_FIELD_API_NAMES,
} from '../constants/crmFieldMapping';

interface MapMatchingHubSpotFields {
  companyToAccount?: CrmFieldMatch;
  contactToContact?: CrmFieldMatch;
  dealToQuote?: CrmFieldMatch;
  productToProducts?: CrmFieldMatch;
}

interface MapMatchingSfdcFields {
  opportunityToQuote?: CrmFieldMatch;
  contactToContact?: CrmFieldMatch;
  accountToAccount?: CrmFieldMatch;
  accountToAccountFromMn?: CrmFieldMatch;
  productToProduct?: CrmFieldMatch;
  offeringToProduct?: CrmFieldMatch;
}

export const convertCustomFieldTypeToCrmType = (
  type: CustomFieldTypeEnum,
): FieldMappingBaseFieldType['dataType'] => {
  let dataType = type.toLowerCase() as FieldMappingBaseFieldType['dataType'];
  if (type === CustomFieldTypeEnum.CHECKBOX) {
    dataType = 'boolean';
  } else if (
    type === CustomFieldTypeEnum.SINGLE_LINE_TEXT ||
    type === CustomFieldTypeEnum.DROPDOWN
  ) {
    dataType = 'text';
  }
  const parseResult =
    FieldMappingBaseFieldSchema.shape.dataType.safeParse(dataType);
  return parseResult.success ? parseResult.data : 'text';
};

export const stringifyDisplayLabel = (displayLabel: string) => {
  const pattern = /_[0-9a-f]{8}$/;
  return pattern.test(displayLabel)
    ? displayLabel
        .replace(pattern, '')
        .split('_')
        .map((v) => startCase(v))
        .join(' ')
    : displayLabel;
};

export const mapMatchingHubSpotFields = (
  crmFields?: FieldMappingType,
  monetizeNowFields?: FieldMappingType,
): MapMatchingHubSpotFields => {
  if (!crmFields || !monetizeNowFields) {
    return {};
  }
  let response: Partial<MapMatchingHubSpotFields> = {};
  if ('companies' in crmFields && 'Account' in monetizeNowFields) {
    response = {
      ...response,
      companyToAccount: {
        crm: crmFields.companies!,
        monetize: monetizeNowFields.Account!,
      },
    };
  }
  if ('contacts' in crmFields && 'Contact' in monetizeNowFields) {
    response = {
      ...response,
      contactToContact: {
        crm: crmFields.contacts || [],
        monetize: monetizeNowFields.Contact || [],
      },
    };
  }
  if ('deals' in crmFields && 'Quote' in monetizeNowFields) {
    response = {
      ...response,
      dealToQuote: {
        crm: crmFields.deals || [],
        monetize: monetizeNowFields.Quote || [],
      },
    };
  }
  if ('products' in crmFields && 'Product' in monetizeNowFields) {
    response = {
      ...response,
      productToProducts: {
        crm: crmFields.products || [],
        monetize: monetizeNowFields.Product || [],
      },
    };
  }
  return response;
};

export const mapMatchingSalesforceFields = (
  crmFields?: FieldMappingType,
  monetizeNowFields?: FieldMappingType,
): MapMatchingSfdcFields => {
  if (!crmFields || !monetizeNowFields) {
    return {};
  }
  let response: Partial<MapMatchingSfdcFields> = {};
  if ('Opportunity' in crmFields && 'Quote' in monetizeNowFields) {
    response = {
      ...response,
      opportunityToQuote: {
        crm: crmFields.Opportunity || [],
        monetize: monetizeNowFields.Quote || [],
      },
    };
  }
  if ('Contact' in crmFields && 'Contact' in monetizeNowFields) {
    response = {
      ...response,
      contactToContact: {
        crm: crmFields.Contact || [],
        monetize: monetizeNowFields.Contact || [],
      },
    };
  }
  if ('Account' in crmFields && 'Account' in monetizeNowFields) {
    response = {
      ...response,
      accountToAccount: {
        crm: crmFields.Account || [],
        monetize: monetizeNowFields.Account || [],
      },
      // Same field lists, minus the Salesforce fields we populate ourselves,
      // plus the outbound-only rollups. The accordion swaps source/target
      // based on its `direction` prop.
      accountToAccountFromMn: {
        crm: (crmFields.Account || []).filter(
          ({ fieldApiName }) =>
            !SF_ACCOUNT_OUTBOUND_EXCLUDED_FIELD_API_NAMES.has(fieldApiName) &&
            !fieldApiName.startsWith('monetize__'),
        ),
        monetize: (monetizeNowFields.Account || []).concat(
          MN_ACCOUNT_OUTBOUND_FIELDS_FOR_SALESFORCE,
        ),
      },
    };
  }
  if ('Product2' in crmFields && 'Product' in monetizeNowFields) {
    response = {
      ...response,
      productToProduct: {
        crm: crmFields.Product2 || [],
        monetize: monetizeNowFields.Product || [],
      },
    };
  }
  if ('Product2' in crmFields && 'Offering' in monetizeNowFields) {
    response = {
      ...response,
      offeringToProduct: {
        crm: crmFields.Product2 || [],
        monetize: monetizeNowFields.Offering || [],
      },
    };
  }
  return response;
};

/**
 * Stand-in CRM field metadata for when the CRM fields request fails (e.g. the
 * connection's credentials are invalid), so saved mappings can still render.
 * Every mapped CRM object gets an entry so each section shows, holding just the
 * CRM-side fields of the saved mappings: the source of a CRM -> MonetizeNow
 * mapping, the target of a MonetizeNow -> CRM one.
 */
export const buildPlaceholderCrmFields = (
  system: keyof typeof CRM_FIELD_MAPPING_OBJECT_TYPES,
  fieldMappings?: FieldMappingsResType,
): FieldMappingType => {
  const placeholderFields = { system } as FieldMappingType;
  CRM_FIELD_MAPPING_OBJECT_TYPES[system].forEach((objectType) => {
    placeholderFields[objectType] = [];
  });

  const crmSideFields = [
    ...(fieldMappings?.toMn.map(({ source }) => source) ?? []),
    ...(fieldMappings?.fromMn.map(({ target }) => target) ?? []),
  ];
  crmSideFields.forEach(
    ({ objectType, fieldApiName, displayLabel, dataType }) => {
      const fields = (placeholderFields[objectType] ??= []);
      if (!fields.some((field) => field.fieldApiName === fieldApiName)) {
        fields.push({
          fieldApiName,
          displayLabel: displayLabel || fieldApiName,
          dataType,
        });
      }
    },
  );
  return placeholderFields;
};

/**
 * Transform crmFields and monetizeNowFields to a normalized format that
 * can be used in the form
 */
export const transformToDisplayLabelLookupRecord = (
  crmFields?: FieldMappingType,
  monetizeNowFields?: FieldMappingType,
) => {
  const performMapping = (
    data: FieldMappingType,
    baseData: Record<string, Record<string, string>> = {},
  ) => {
    return Object.keys(data).reduce((result, key) => {
      const fields = data[key as FieldMappingObjectType];
      if (isArray(fields)) {
        result[key] = {
          ...result[key],
          ...fields.reduce(
            (res, field) => {
              res[field.fieldApiName] = stringifyDisplayLabel(
                field.displayLabel,
              );
              return res;
            },
            {} as Record<string, string>,
          ),
        };
      }
      return result;
    }, baseData);
  };

  const output: Record<string, Record<string, string>> = {};
  if (crmFields) {
    performMapping(crmFields, output);
  }
  if (monetizeNowFields) {
    performMapping(monetizeNowFields, output);
  }
  return output;
};

const getFieldWithDisplayLabel = (
  field: FieldMappingCreateFieldType,
  displayLabelLookupRecord: Record<string, Record<string, string>>,
): FieldMappingCreateFieldType => {
  let displayLabel =
    displayLabelLookupRecord?.[field.objectType]?.[field.fieldApiName];
  if (!displayLabel) {
    displayLabel = field.fieldApiName;
  }
  return {
    ...field,
    displayLabel,
  };
};

export const convertToField = (
  field: FieldMappingsResFieldType,
  displayLabelLookupRecord: Record<string, Record<string, string>>,
): FieldMappingCreateReqType => {
  const source = getFieldWithDisplayLabel(
    field.source,
    displayLabelLookupRecord,
  );
  let targetFieldApiName = field.target.fieldApiName;
  if (field.mode === 'by_reference_with_lookup_filter') {
    const [firstPart, secondPart] = targetFieldApiName.split('.');
    switch (firstPart) {
      case 'legalEntityId':
        targetFieldApiName = `legalEntityId.${field.via?.fieldApiName}`;
        break;
      case 'contacts':
        targetFieldApiName = `${firstPart}.${secondPart}`;
        break;
    }
  }

  const target = getFieldWithDisplayLabel(
    {
      ...field.target,
      fieldApiName: targetFieldApiName,
    },
    displayLabelLookupRecord,
  );

  return field.mode === 'by_reference_with_lookup_filter'
    ? {
        mode: field.mode,
        source: {
          fieldApiName: source.fieldApiName,
          displayLabel: source.displayLabel,
          dataType: source.dataType,
        },
        target: {
          fieldApiName: target.fieldApiName,
          displayLabel: target.displayLabel,
          dataType: target.dataType,
        },
        via: field.via,
      }
    : {
        mode: field.mode,
        source: {
          fieldApiName: source.fieldApiName,
          displayLabel: source.displayLabel,
          dataType: source.dataType,
        },
        target: {
          fieldApiName: target.fieldApiName,
          displayLabel: target.displayLabel,
          dataType: target.dataType,
        },
      };
};

export const mapExistingFieldMapping = (
  data?: FieldMappingsResType,
  crmFields?: FieldMappingType,
  monetizeNowFields?: FieldMappingType,
) => {
  const response = {
    userDefinedCompaniesToAccount: [] as FieldMappingCreateReqType[],
    userDefinedContactsToContact: [] as FieldMappingCreateReqType[],
    userDefinedDealsToQuote: [] as FieldMappingCreateReqType[],
    userDefinedUOpportunityToQuote: [] as FieldMappingCreateReqType[],
    userDefinedUContactToContact: [] as FieldMappingCreateReqType[],
    userDefinedUAccountToAccount: [] as FieldMappingCreateReqType[],
  };
  if (!data) {
    return response;
  }

  const displayLabelLookupRecord = transformToDisplayLabelLookupRecord(
    crmFields,
    monetizeNowFields,
  );

  return data.toMn.reduce((result, field) => {
    const convertToFieldResult = convertToField(
      field,
      displayLabelLookupRecord,
    );

    if (
      field.source.objectType === 'companies' &&
      field.target.objectType === 'Account'
    ) {
      result.userDefinedCompaniesToAccount.push(convertToFieldResult);
    } else if (
      field.source.objectType === 'contacts' &&
      field.target.objectType === 'Contact'
    ) {
      result.userDefinedContactsToContact.push(convertToFieldResult);
    } else if (
      field.source.objectType === 'deals' &&
      field.target.objectType === 'Quote'
    ) {
      result.userDefinedDealsToQuote.push(convertToFieldResult);
    } else if (
      field.source.objectType === 'Opportunity' &&
      field.target.objectType === 'Quote'
    ) {
      result.userDefinedUOpportunityToQuote.push(convertToFieldResult);
    } else if (
      field.source.objectType === 'Contact' &&
      field.target.objectType === 'Contact'
    ) {
      result.userDefinedUContactToContact.push(convertToFieldResult);
    } else if (
      field.source.objectType === 'Account' &&
      field.target.objectType === 'Account'
    ) {
      result.userDefinedUAccountToAccount.push(convertToFieldResult);
    }
    return result;
  }, response);
};

/**
 * Reads the MonetizeNow -> CRM mappings (data.fromMn) for Salesforce. Only
 * Account -> Account, Product -> Product2 and Offering -> Product2 are
 * supported in this direction.
 */
export const mapExistingSalesforceFieldMappingFromMn = (
  data?: FieldMappingsResType,
  crmFields?: FieldMappingType,
  monetizeNowFields?: FieldMappingType,
) => {
  const response = {
    userDefinedAccountToAccountFromMn: [] as FieldMappingCreateReqType[],
    userDefinedProductToProduct: [] as FieldMappingCreateReqType[],
    userDefinedOfferingToProduct: [] as FieldMappingCreateReqType[],
  };
  if (!data) {
    return response;
  }

  // The outbound-only Account fields live outside `monetizeNowFields`, so they
  // have to be merged in here as well as into the dropdown in
  // `mapMatchingSalesforceFields` — otherwise a saved mapping using one of them
  // misses the lookup and falls back to rendering its raw field API name.
  const displayLabelLookupRecord = transformToDisplayLabelLookupRecord(
    crmFields,
    {
      ...monetizeNowFields,
      Account: (monetizeNowFields?.Account || []).concat(
        MN_ACCOUNT_OUTBOUND_FIELDS_FOR_SALESFORCE,
      ),
    } as FieldMappingType,
  );

  return data.fromMn.reduce((result, field) => {
    const convertToFieldResult = convertToField(
      field,
      displayLabelLookupRecord,
    );

    if (
      field.source.objectType === 'Account' &&
      field.target.objectType === 'Account'
    ) {
      result.userDefinedAccountToAccountFromMn.push(convertToFieldResult);
    } else if (
      field.source.objectType === 'Product' &&
      field.target.objectType === 'Product2'
    ) {
      result.userDefinedProductToProduct.push(convertToFieldResult);
    } else if (
      field.source.objectType === 'Offering' &&
      field.target.objectType === 'Product2'
    ) {
      result.userDefinedOfferingToProduct.push(convertToFieldResult);
    }
    return result;
  }, response);
};

/**
 * Reads the MonetizeNow -> CRM mappings (data.fromMn) for HubSpot. Only
 * Product -> products is supported in this direction.
 */
export const mapExistingHubSpotFieldMappingFromMn = (
  data?: FieldMappingsResType,
  crmFields?: FieldMappingType,
  monetizeNowFields?: FieldMappingType,
) => {
  const response = {
    userDefinedProductToProducts: [] as FieldMappingCreateReqType[],
  };
  if (!data) {
    return response;
  }

  const displayLabelLookupRecord = transformToDisplayLabelLookupRecord(
    crmFields,
    monetizeNowFields,
  );

  return data.fromMn.reduce((result, field) => {
    if (
      field.source.objectType === 'Product' &&
      field.target.objectType === 'products'
    ) {
      result.userDefinedProductToProducts.push(
        convertToField(field, displayLabelLookupRecord),
      );
    }
    return result;
  }, response);
};

export const transformCrmFields = (
  matchedFields: CrmFieldMatch,
  objectType: 'crm' | 'monetize',
): FieldMappingBaseFieldType[] => {
  const fields =
    matchedFields[objectType]?.map(
      ({ displayLabel, fieldApiName, dataType, isCustom }) => ({
        displayLabel: stringifyDisplayLabel(displayLabel),
        fieldApiName,
        dataType,
        isCustom,
      }),
    ) ?? [];
  return orderBy(
    fields,
    [({ displayLabel }) => displayLabel.toLowerCase()],
    ['asc'],
  ) satisfies FieldMappingBaseFieldType[];
};

export const transformToFieldMappingBatchUpsertPayloadType = (
  sourceSystem: FieldMappingSystemType,
  sourceEntity: FieldMappingObjectType,
  targetSystem: FieldMappingSystemType,
  targetEntity: FieldMappingObjectType,
  payloads: FieldMappingCreateReqType[],
): FieldMappingBatchUpsertPayloadType => {
  return payloads.reduce(
    (result, field) => {
      let mappingField = {
        source: {
          fieldApiName: field.source.fieldApiName,
        },
        target: {
          fieldApiName: field.target.fieldApiName,
        },
        mode: field.mode,
      } as FieldMappingBatchUpsertPayloadMappingFieldType;

      if (field.mode === 'by_reference_with_lookup_filter') {
        const [firstPart, secondPart] = field.target.fieldApiName.split('.');
        switch (firstPart) {
          case 'legalEntityId':
            mappingField = {
              ...mappingField,
              target: {
                fieldApiName: firstPart,
              },
            };
            break;
          case 'contacts':
            mappingField = {
              ...mappingField,
              target: {
                fieldApiName: `${firstPart}.${secondPart}`,
              },
            };
            break;
        }

        if (field.via) {
          mappingField.via = {
            objectType: field.via.objectType,
            fieldApiName: field.via.fieldApiName,
          };
        }
      }

      result.mappings.push(mappingField);
      return result;
    },
    {
      sourceSystem,
      sourceEntity,
      targetSystem,
      targetEntity,
      mappings: [],
    } as FieldMappingBatchUpsertPayloadType,
  );
};
